import { useEffect, useRef, useState } from 'react';
import { FaIndustry, FaTshirt, FaUsers, FaShippingFast, FaCheckCircle, FaTags } from 'react-icons/fa';
import { MdHighQuality } from 'react-icons/md';
import { GiClothes } from 'react-icons/gi';

function About() {
    const [isVisible, setIsVisible] = useState({});
    const observerRefs = useRef([]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setIsVisible((prev) => ({ ...prev, [entry.target.dataset.section]: true }));
                    }
                });
            },
            { threshold: 0.1 }
        );

        observerRefs.current.forEach((ref) => {
            if (ref) observer.observe(ref);
        });

        return () => observer.disconnect();
    }, []);

    return (
        <div className="bg-gradient-to-b from-gray-50 to-white">
            {/* Hero Section */}
            <section className="relative bg-primary-600 text-white py-20 px-4 overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-10 left-10 w-72 h-72 bg-white rounded-full blur-3xl animate-pulse"></div>
                    <div className="absolute bottom-10 right-10 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse delay-700"></div>
                </div>

                <div className="max-w-6xl mx-auto text-center relative z-10">
                    <h1 className="text-4xl md:text-6xl font-bold mb-6 animate-fade-in">
                        Welcome to <span className="text-yellow-300">SS Tex</span>
                    </h1>
                    <p className="text-xl md:text-2xl mb-4 opacity-90">
                        Tiruppur's Trusted Garment Manufacturer
                    </p>
                    <p className="text-lg md:text-xl max-w-3xl mx-auto opacity-80">
                        Leading textile and garment manufacturing company with 7+ years of excellence in producing high-quality apparel
                    </p>
                </div>
            </section>

            {/* Stats Section */}
            <section
                ref={(el) => (observerRefs.current[0] = el)}
                data-section="stats"
                className="py-16 px-4 bg-white"
            >
                <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
                    <StatCard
                        icon={<FaIndustry />}
                        number={7}
                        suffix="+"
                        label="Years Experience"
                        isVisible={isVisible.stats}
                    />
                    <StatCard
                        icon={<FaUsers />}
                        number={2000}
                        suffix="+"
                        label="Wholesale Clients"
                        isVisible={isVisible.stats}
                    />
                    <StatCard
                        icon={<FaShippingFast />}
                        number={50000}
                        suffix="+"
                        label="Retail Customers"
                        isVisible={isVisible.stats}
                    />
                    <StatCard
                        icon={<FaTshirt />}
                        number={100}
                        suffix="%"
                        label="Quality Assured"
                        isVisible={isVisible.stats}
                    />
                </div>
            </section>

            {/* What We Do Section */}
            <section
                ref={(el) => (observerRefs.current[1] = el)}
                data-section="whatwedo"
                className="py-16 px-4 bg-gray-50"
            >
                <div className="max-w-6xl mx-auto">
                    <h2 className={`text-3xl md:text-4xl font-bold text-center text-gray-800 mb-12 transition-all duration-1000 ${isVisible.whatwedo ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                        What We Do
                    </h2>

                    <div className="grid md:grid-cols-3 gap-8">
                        <FeatureCard
                            icon={<GiClothes className="text-5xl" />}
                            title="Complete Garment Manufacturing"
                            description="From fabric sourcing, cutting, stitching, and finishing to packaging and dispatch - all under one roof."
                            delay="100"
                            isVisible={isVisible.whatwedo}
                        />
                        <FeatureCard
                            icon={<FaTags className="text-5xl" />}
                            title="Bulk & Custom Production"
                            description="Meeting small to large-scale orders with consistent quality and timely delivery."
                            delay="200"
                            isVisible={isVisible.whatwedo}
                        />
                        <FeatureCard
                            icon={<MdHighQuality className="text-5xl" />}
                            title="OEM / Private Label Services"
                            description="Producing garments under clients' own brands with complete customization."
                            delay="300"
                            isVisible={isVisible.whatwedo}
                        />
                    </div>
                </div>
            </section>

            {/* Product Range Section */}
            <section
                ref={(el) => (observerRefs.current[2] = el)}
                data-section="products"
                className="py-16 px-4 bg-white"
            >
                <div className="max-w-6xl mx-auto">
                    <h2 className={`text-3xl md:text-4xl font-bold text-center text-gray-800 mb-12 transition-all duration-1000 ${isVisible.products ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                        Our Product Range
                    </h2>

                    <div className="grid md:grid-cols-3 gap-8">
                        <ProductCategory
                            title="Men's Collection"
                            items={["T-Shirts", "Track Pants", "Tracksuits", "Polos"]}
                            color="bg-blue-500"
                            isVisible={isVisible.products}
                            delay="100"
                        />
                        <ProductCategory
                            title="Women's Collection"
                            items={["Feeding Kurtis & Sets", "Tops & Bottom Sets", "Panties & Briefs", "Maternity Topwear"]}
                            color="bg-pink-500"
                            isVisible={isVisible.products}
                            delay="200"
                        />
                        <ProductCategory
                            title="Kids Collection"
                            items={["T-Shirts for Boys", "T-Shirts for Girls", "Polos", "Comfortable Wear"]}
                            color="bg-green-500"
                            isVisible={isVisible.products}
                            delay="300"
                        />
                    </div>

                    <p className={`text-center text-gray-600 mt-8 text-lg transition-all duration-1000 delay-500 ${isVisible.products ? 'opacity-100' : 'opacity-0'}`}>
                        ✨ All sizes, all colors, customizable fabrics and prints available
                    </p>
                </div>
            </section>

            {/* Key Highlights Section */}
            <section
                ref={(el) => (observerRefs.current[3] = el)}
                data-section="highlights"
                className="py-16 px-4 bg-gradient-to-br from-primary-600 to-primary-700 text-white"
            >
                <div className="max-w-6xl mx-auto">
                    <h2 className={`text-3xl md:text-4xl font-bold text-center mb-12 transition-all duration-1000 ${isVisible.highlights ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                        Why Choose SS Tex?
                    </h2>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <HighlightCard
                            icon={<FaIndustry />}
                            title="Direct Factory Outlet"
                            description="No middlemen, ensuring the best market price"
                            isVisible={isVisible.highlights}
                            delay="100"
                        />
                        <HighlightCard
                            icon={<FaTags />}
                            title="Unbeatable Wholesale Pricing"
                            description="Transparent and volume-based discounts"
                            isVisible={isVisible.highlights}
                            delay="200"
                        />
                        <HighlightCard
                            icon={<FaShippingFast />}
                            title="All-India Delivery"
                            description="Fast logistics network for pan-India orders"
                            isVisible={isVisible.highlights}
                            delay="300"
                        />
                        <HighlightCard
                            icon={<FaUsers />}
                            title="Trusted by Thousands"
                            description="2,000+ wholesale clients & 50,000+ retail customers"
                            isVisible={isVisible.highlights}
                            delay="400"
                        />
                        <HighlightCard
                            icon={<FaCheckCircle />}
                            title="Quality Commitment"
                            description="Multi-stage inspection for every product"
                            isVisible={isVisible.highlights}
                            delay="500"
                        />
                        <HighlightCard
                            icon={<MdHighQuality />}
                            title="Premium Standards"
                            description="Factory-direct quality without compromise"
                            isVisible={isVisible.highlights}
                            delay="600"
                        />
                    </div>
                </div>
            </section>

            {/* Vision Section */}
            <section
                ref={(el) => (observerRefs.current[4] = el)}
                data-section="vision"
                className="py-16 px-4 bg-white"
            >
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className={`text-3xl md:text-4xl font-bold text-gray-800 mb-6 transition-all duration-1000 ${isVisible.vision ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                        Our Vision
                    </h2>
                    <p className={`text-lg md:text-xl text-gray-600 leading-relaxed transition-all duration-1000 delay-200 ${isVisible.vision ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                        To deliver <span className="text-primary-600 font-semibold">premium-quality garments</span> at factory-direct prices,
                        empowering retailers and resellers across India to maximize value without compromising on style or comfort.
                    </p>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 px-4 bg-gradient-to-r from-primary-500 to-primary-700 text-white">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl md:text-4xl font-bold mb-6">
                        Ready to Partner with Us?
                    </h2>
                    <p className="text-lg md:text-xl mb-8 opacity-90">
                        Experience factory-direct quality and pricing. Join thousands of satisfied customers across India.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <a
                            href="/products"
                            className="bg-white text-primary-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-all transform hover:scale-105 shadow-lg"
                        >
                            Browse Products
                        </a>
                        <a
                            href="/contact"
                            className="bg-transparent border-2 border-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-primary-600 transition-all transform hover:scale-105"
                        >
                            Contact Us
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
}

// Animated Counter Component
const AnimatedCounter = ({ end, duration = 2000, isVisible, suffix = "" }) => {
    const [count, setCount] = useState(0);

    useEffect(() => {
        if (!isVisible) return;

        let startTime;
        const animate = (currentTime) => {
            if (!startTime) startTime = currentTime;
            const progress = Math.min((currentTime - startTime) / duration, 1);

            setCount(Math.floor(progress * end));

            if (progress < 1) {
                requestAnimationFrame(animate);
            }
        };

        requestAnimationFrame(animate);
    }, [end, duration, isVisible]);

    return <span>{count.toLocaleString()}{suffix}</span>;
};

// Stat Card Component
const StatCard = ({ icon, number, suffix, label, isVisible }) => (
    <div className={`text-center transform transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
        <div className="text-primary-600 flex justify-center mb-3 text-4xl">
            {icon}
        </div>
        <div className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
            <AnimatedCounter end={number} isVisible={isVisible} suffix={suffix} />
        </div>
        <div className="text-gray-600 text-sm md:text-base">{label}</div>
    </div>
);

// Feature Card Component
const FeatureCard = ({ icon, title, description, delay, isVisible }) => (
    <div
        className={`bg-white p-6 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        style={{ transitionDelay: `${delay}ms` }}
    >
        <div className="text-primary-600 mb-4 flex justify-center">
            {icon}
        </div>
        <h3 className="text-xl font-bold text-gray-800 mb-3 text-center">{title}</h3>
        <p className="text-gray-600 text-center leading-relaxed">{description}</p>
    </div>
);

// Product Category Component
const ProductCategory = ({ title, items, color, isVisible, delay }) => (
    <div
        className={`bg-white rounded-xl shadow-lg overflow-hidden transform transition-all duration-700 hover:scale-105 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        style={{ transitionDelay: `${delay}ms` }}
    >
        <div className={`${color} text-white py-4 px-6`}>
            <h3 className="text-2xl font-bold text-center">{title}</h3>
        </div>
        <div className="p-6">
            <ul className="space-y-3">
                {items.map((item, index) => (
                    <li key={index} className="flex items-center text-gray-700">
                        <FaCheckCircle className="text-primary-600 mr-3 flex-shrink-0" />
                        <span>{item}</span>
                    </li>
                ))}
            </ul>
        </div>
    </div>
);

// Highlight Card Component
const HighlightCard = ({ icon, title, description, isVisible, delay }) => (
    <div
        className={`bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20 hover:bg-white/20 transition-all duration-500 transform hover:scale-105 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        style={{ transitionDelay: `${delay}ms` }}
    >
        <div className="text-4xl mb-4">
            {icon}
        </div>
        <h3 className="text-xl font-bold mb-2">{title}</h3>
        <p className="text-white/80">{description}</p>
    </div>
);

export default About;